import { RRule } from 'rrule'

/**
 * Calendar schedules for habits, expressed as an RFC 5545 RRULE (for example
 * `FREQ=YEARLY` or `FREQ=WEEKLY;BYDAY=MO,WE,FR`) anchored to the habit's first
 * occurrence. The anchor's local wall-clock time is the time of day every
 * occurrence lands on, so rules are evaluated in "floating" local time and only
 * converted to instants at the end; that keeps 09:00 at 09:00 across DST changes.
 */

/** Upper bound on occurrences walked per evaluation (~27 years of a daily rule). */
const MAX_OCCURRENCES = 10_000

export type CalendarScheduleHabit = {
   /** The RRULE body, without `RRULE:` or `DTSTART`. */
   scheduleRule: string
   /** The first occurrence (epoch ms); its local time is the time of day. */
   anchorAt: number
   /** Completion instants (epoch ms), any order. */
   completionHistory: readonly number[]
   lastCompletedAt?: number | null
}

/**
 * The offset of a time zone from UTC at an instant, in ms.
 *
 * @param ms - The instant.
 * @param timeZone - The IANA zone.
 * @returns Local wall-clock minus UTC.
 */
function zoneOffsetMs(ms: number, timeZone: string) {
   const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
      hourCycle: 'h23',
   }).formatToParts(new Date(ms))
   const read = (type: string) => Number(parts.find(part => part.type === type)?.value)
   const wall = Date.UTC(
      read('year'),
      read('month') - 1,
      read('day'),
      read('hour'),
      read('minute'),
      read('second'),
   )
   return wall - Math.floor(ms / 1000) * 1000
}

/**
 * Converts an instant to its local wall-clock time, encoded as a UTC timestamp.
 *
 * @param ms - The instant.
 * @param timeZone - The IANA zone.
 * @returns The "floating" timestamp.
 */
function toFloating(ms: number, timeZone: string) {
   return ms + zoneOffsetMs(ms, timeZone)
}

/**
 * Converts a floating wall-clock timestamp back to an instant in a time zone.
 *
 * @param floating - The floating timestamp from {@link toFloating}.
 * @param timeZone - The IANA zone.
 * @returns The instant.
 */
function fromFloating(floating: number, timeZone: string) {
   // Re-read the offset at the guessed instant so DST transitions resolve correctly.
   const guess = floating - zoneOffsetMs(floating, timeZone)
   return floating - zoneOffsetMs(guess, timeZone)
}

/**
 * Returns the time zone the current runtime uses for local time.
 *
 * @returns An IANA zone name (UTC when unknown).
 */
export function getRuntimeTimeZone() {
   return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
}

/**
 * Validates and normalizes an RRULE body (an optional `RRULE:` prefix is
 * dropped; `DTSTART` is not allowed because the anchor is stored separately).
 *
 * @param value - The candidate rule.
 * @returns The canonical rule string, or null when it can't be parsed.
 */
export function normalizeScheduleRule(value: unknown) {
   if (typeof value !== 'string') {
      return null
   }
   const body = value.trim().replace(/^RRULE:/i, '')
   if (!body || /DTSTART|\n/i.test(body)) {
      return null
   }
   try {
      const options = RRule.parseString(body)
      if (options.freq === undefined) {
         return null
      }
      // Sub-daily frequencies don't make sense for habits.
      if (options.freq > RRule.DAILY) {
         return null
      }
      return RRule.optionsToString(options).replace(/^RRULE:/, '')
   } catch {
      return null
   }
}

/**
 * Builds the rule, anchored at the floating anchor time.
 *
 * @param scheduleRule - The RRULE body.
 * @param anchorFloating - The floating anchor timestamp.
 * @returns The rule, or null when invalid.
 */
function buildRule(scheduleRule: string, anchorFloating: number) {
   const normalized = normalizeScheduleRule(scheduleRule)
   if (!normalized) {
      return null
   }
   return new RRule({
      ...RRule.parseString(normalized),
      dtstart: new Date(anchorFloating),
   })
}

/**
 * Lists floating occurrence timestamps from the anchor through a few past `untilFloating`:
 * an early completion settles the next one, and overdue checks need the one after that.
 *
 * @param rule - The anchored rule.
 * @param untilFloating - The latest relevant floating time.
 * @param extra - How many occurrences past `untilFloating` to include.
 * @returns Ascending floating occurrence timestamps.
 */
function occurrencesThrough(rule: RRule, untilFloating: number, extra: number) {
   const occurrences: number[] = []
   let beyond = 0
   rule.all((date, index) => {
      if (index >= MAX_OCCURRENCES) {
         return false
      }
      const floating = date.getTime()
      occurrences.push(floating)
      if (floating > untilFloating) {
         beyond += 1
      }
      return beyond < extra
   })
   return occurrences
}

/**
 * Walks completions over the occurrences: each completion settles the occurrence
 * that was due (early completions count, so a birthday ticked off two days ahead
 * is done for this year) and moves past any occurrences it was late for.
 * Completions before the first occurrence don't count: clients move the anchor
 * to the next occurrence after completing, so earlier completions are already
 * reflected in it.
 *
 * @param habit - The calendar habit.
 * @param now - The reference instant.
 * @param timeZone - The IANA zone the schedule is evaluated in.
 * @returns The due occurrence and the one after it (floating), or null when the rule is exhausted.
 */
function resolveDueOccurrence(
   habit: CalendarScheduleHabit,
   now: number,
   timeZone: string,
) {
   const anchorFloating = toFloating(habit.anchorAt, timeZone)
   const rule = buildRule(habit.scheduleRule, anchorFloating)
   if (!rule) {
      return null
   }
   const completions = [...habit.completionHistory]
   if (
      typeof habit.lastCompletedAt === 'number' &&
      !completions.includes(habit.lastCompletedAt)
   ) {
      completions.push(habit.lastCompletedAt)
   }
   const floatingCompletions = completions
      .map(ms => toFloating(ms, timeZone))
      .sort((a, b) => a - b)
   const latest = Math.max(toFloating(now, timeZone), floatingCompletions.at(-1) ?? 0)
   // Each completion can settle at most one occurrence past `latest` (repeated
   // early completions), plus the due one and the one after it.
   const occurrences = occurrencesThrough(
      rule,
      latest,
      2 + Math.min(floatingCompletions.length, 50),
   )
   if (occurrences.length === 0) {
      return null
   }

   let windowStart = occurrences[0]! - 1
   let dueIndex = 0
   for (const completion of floatingCompletions) {
      if (completion <= windowStart) {
         continue
      }
      dueIndex += 1
      while (dueIndex < occurrences.length && occurrences[dueIndex]! <= completion) {
         dueIndex += 1
      }
      windowStart = completion
      if (dueIndex >= occurrences.length) {
         return null
      }
   }
   return {
      due: occurrences[dueIndex]!,
      following: occurrences[dueIndex + 1] ?? null,
   }
}

/**
 * When a calendar habit is next due.
 *
 * @param habit - The calendar habit.
 * @param now - The reference instant (defaults to now).
 * @param timeZone - The IANA zone (defaults to the runtime's).
 * @returns The due instant in epoch ms, or null when the rule has no further occurrences.
 */
export function getCalendarNextDueAt(
   habit: CalendarScheduleHabit,
   now = Date.now(),
   timeZone = getRuntimeTimeZone(),
) {
   const resolved = resolveDueOccurrence(habit, now, timeZone)
   return resolved ? fromFloating(resolved.due, timeZone) : null
}

/**
 * Whether a calendar habit missed a whole occurrence: the due one and the one
 * after it have both passed without a completion.
 *
 * @param habit - The calendar habit.
 * @param now - The reference instant (defaults to now).
 * @param timeZone - The IANA zone (defaults to the runtime's).
 * @returns True when overdue.
 */
export function isCalendarHabitOverdue(
   habit: CalendarScheduleHabit,
   now = Date.now(),
   timeZone = getRuntimeTimeZone(),
) {
   const resolved = resolveDueOccurrence(habit, now, timeZone)
   return (
      resolved !== null &&
      resolved.following !== null &&
      fromFloating(resolved.following, timeZone) <= now
   )
}

/**
 * A rough interval in days between occurrences, for clients that only understand
 * "every N days" schedules (and for streak math).
 *
 * @param scheduleRule - The RRULE body.
 * @returns Days between occurrences, clamped to 1-365.
 */
export function approximateScheduleDays(scheduleRule: string) {
   const normalized = normalizeScheduleRule(scheduleRule)
   if (!normalized) {
      return 1
   }
   const options = RRule.parseString(normalized)
   const interval = options.interval ?? 1
   const byDayCount = Array.isArray(options.byweekday) ? options.byweekday.length : 0
   let days: number
   switch (options.freq) {
      case RRule.YEARLY:
         days = 365 * interval
         break
      case RRule.MONTHLY:
         days = 30 * interval
         break
      case RRule.WEEKLY:
         days = byDayCount > 1 ? Math.max(1, Math.floor(7 / byDayCount)) : 7 * interval
         break
      default:
         days = interval
   }
   return Math.max(1, Math.min(365, Math.round(days)))
}
